import type { TreeStatistics } from "@/types/tree-statistics";
import type { DashboardOverview } from "@/types/dashboard";
import type { Bairro } from "@/types/bairros";
import type { AreaTreeInventory } from "@/types/tree-inventory";
import type {
  AreaTreeInventoryApi,
  TreeStatisticsApi,
  BairroApi,
  DashboardOverviewApi,
  CampaignMemberApi,
  CampaignQuadraProgressApi,
  CampaignSummaryApi,
  TreeCreateResponseApi,
  ArborCompensationApi,
  ArborEvidenceApi,
  ArborHealthAssessmentApi,
  ArborHealthFindingApi,
  ArborInspectionDetailApi,
  ArborInspectionSummaryApi,
  ArborInterferenceApi,
  ArborMeasurementApi,
  ArborMechanicalFindingApi,
  ArborRecommendationApi,
  ArborRiskAssessmentApi,
  ArborRiskTargetApi,
  ArborStemMeasurementApi,
  AreaPilotoApi,
  CamadaFeatureApi,
  CamadaGeoApi,
  DashboardSummaryApi,
  DetectedObjectApi,
  DocumentApi,
  FieldValidationNoteApi,
  InspectionApi,
  InspectionQuadraLinkApi,
  PartyApi,
  ProjectApi,
  QualitySummaryApi,
  QuadraApi,
  RascunhoFeicaoApi,
  RascunhoPublishResultApi,
  SpeciesApi,
  TerritorialIndicatorsSummaryApi,
  TerritorialInspectionDetailApi,
  TerritorialInspectionSummaryApi,
  TreeApi,
  TreeMetrics3DApi,
  ValidationIssueApi,
} from "@/lib/api";
import type {
  TerritorialCoverage,
  TerritorialDetectedObject,
  TerritorialFieldValidationNote,
  TerritorialIndicator,
  TerritorialIndicatorsSummary,
  TerritorialInspectionDetail,
  TerritorialInspectionSummary,
  TerritorialLandUse,
  TerritorialRecommendation,
  TerritorialRoad,
  TerritorialTreeInventorySummary,
} from "@/types/territorial-inspection";
import type {
  ArborInspectionDetail,
  ArborInspectionSummary,
  Compensation,
  Evidence,
  HealthAssessment,
  HealthFinding,
  Interference,
  MechanicalFinding,
  Recommendation,
  RiskAssessment,
  RiskTarget,
  StemMeasurement,
  TreeMeasurement,
} from "@/types/arboricultural-inspection";
import type { AreaPiloto, InspectionQuadraLink, Quadra } from "@/types/territorial";
import type { CampaignMember, CampaignQuadraProgress, CampaignSummary, TreeCreateResult } from "@/types/campaign";
import type {
  CamadaFeature,
  CamadaGeo,
  RascunhoFeicao,
  RascunhoPublishResult,
  RascunhoStatus,
} from "@/types/editor-territorial";
import type {
  Species,
  Projeto,
  IndividuoArboreo,
  DocumentItem,
  Party,
  Inconsistency,
  ProjectQualityRank,
} from "@/types/ui-models";
import type { Vistoria } from "@/types/vistorias";

export interface TreeMetric3DRow {
  codigoCta: string;
  especie: string;
  projeto: string;
  bairro: string;
  alturaCampo: number | null;
  altura3D: number | null;
  diametroCopaCampo: number | null;
  diametroCopa3D: number | null;
  volumeCopa: number | null;
  inclinacao: number | null;
  confianca: string;
  risco: string;
  status: string;
  fonte3D: string;
}

export interface DashboardSummaryUi {
  totalProjects: number;
  totalTrees: number;
  totalSpecies: number;
  totalDocuments: number;
  totalParties: number;
  totalRrr: number;
  totalValidationIssues: number;
  totalPointCloudAssets: number;
  totalEnvironmentalUnits: number;
  totalCarbonProjects: number;
  status: string;
}

export function mapDashboardSummary(api: DashboardSummaryApi): DashboardSummaryUi {
  return {
    totalProjects: api.total_projects,
    totalTrees: api.total_trees,
    totalSpecies: api.total_species,
    totalDocuments: api.total_documents,
    totalParties: api.total_parties,
    totalRrr: api.total_rrr,
    totalValidationIssues: api.total_validation_issues,
    totalPointCloudAssets: api.total_point_cloud_assets,
    totalEnvironmentalUnits: api.total_environmental_units,
    totalCarbonProjects: api.total_carbon_projects,
    status: api.status,
  };
}

const ORIGIN_LABELS: Record<string, Species["origin"]> = {
  NATIVA: "nativa",
  EXOTICA: "exótica",
  EXOTICA_INVASORA: "exótica invasora",
  ORNAMENTAL: "ornamental",
  FRUTIFERA: "frutífera",
  NAO_IDENTIFICADA: "não identificada",
};

const SIZE_LABELS: Record<string, Species["size"]> = {
  PEQUENO: "pequeno",
  MEDIO: "médio",
  GRANDE: "grande",
  PALMEIRA: "palmeira",
  ARBUSTIVO: "arbustivo",
};

const SPECIES_STATUS_LABELS: Record<string, Species["status"]> = {
  ATIVO: "ativo",
  INATIVO: "inativo",
  EM_REVISAO: "em revisão",
};

export function mapSpeciesFromApi(api: SpeciesApi): Species {
  return {
    id: String(api.id),
    popularName: api.popular_name,
    scientificName: api.scientific_name,
    family: api.family_name ?? "",
    origin: ORIGIN_LABELS[api.origin ?? ""] ?? "não identificada",
    size: SIZE_LABELS[api.tree_size ?? ""] ?? "médio",
    functionalGroup: api.functional_group ?? "",
    woodDensity: api.wood_density ?? undefined,
    carbonFactor: api.preliminary_carbon_factor ?? undefined,
    isProtected: api.protected_species,
    isInvasive: api.invasive_species,
    isFruitful: api.functional_group === "FRUTIFERA",
    isOrnamental: api.functional_group === "ORNAMENTAL",
    status: SPECIES_STATUS_LABELS[api.status] ?? "ativo",
    observations: api.notes ?? undefined,
  };
}

const PROJECT_TYPE_LABELS: Record<string, string> = {
  IMPLANTACAO_CTA_MUNICIPAL: "Implantação CTA Municipal",
  INVENTARIO_ARBOREO_URBANO: "Inventário Arbóreo Urbano",
  INVENTARIO_PRACA: "Inventário de Praça",
  INVENTARIO_PARQUE: "Inventário de Parque",
  INVENTARIO_UC: "Inventário de UC",
  MONITORAMENTO_RISCO: "Monitoramento de Risco",
  COMPENSACAO_AMBIENTAL: "Compensação Ambiental",
  CARBONO_URBANO: "Carbono Urbano",
  LEVANTAMENTO_3D: "Levantamento 3D",
};

export function mapProjectFromApi(api: ProjectApi): Projeto {
  return {
    id: api.project_uuid,
    nome: api.name,
    tipo: PROJECT_TYPE_LABELS[api.project_type] ?? api.project_type,
    cliente: api.client_name ?? "",
    municipio: api.municipality_name ?? "",
    uf: api.uf ?? "",
    status: api.status.toLowerCase(),
    responsavel: "",
    equipe: [],
    metodologia: "FloraIQ-LADM",
    dataInicio: api.created_at,
    dataPrevisao: "",
    dataConclusao: undefined,
    progresso: api.progress_percent,
    totalArvores: api.total_trees,
    vistoriasRealizadas: api.inspection_count,
    vistoriasPendentes: 0,
    arvoresRevisao: 0,
    riscoCritico: api.high_risk_trees,
    especiesIdentificadas: api.species_count,
    fotosAnexadas: 0,
    co2eEstimado: 0,
    areaInventariada: 0,
    areaAbrangencia: "",
    ultimaAtualizacao: api.updated_at,
    alertas: [],
    prioridade: "normal",
  };
}

export function mapTreeFromApi(api: TreeApi): IndividuoArboreo {
  return {
    id: String(api.id),
    codigoCta: api.cta_code ?? "",
    tipo: api.individual_type ?? "",
    especie: api.species_popular_name ?? "",
    nomeCientifico: api.species_scientific_name ?? "",
    nomePopular: api.species_popular_name ?? "",
    projeto: api.project_name ?? "",
    municipio: api.municipality_name ?? "",
    uf: api.municipality_uf ?? "",
    bairro: api.neighborhood ?? "",
    logradouro: api.street ?? "",
    numero: "",
    coordenada: api.latitude != null && api.longitude != null ? `${api.latitude}, ${api.longitude}` : "",
    dap: 0,
    altura: 0,
    diametroCopa: 0,
    estadoFitossanitario: "",
    classeRisco: api.latest_risk_class ?? "",
    recomendacao: "",
    statusRevisao: api.review_status ?? "",
    tecnico: "",
    ultimaVistoria: api.latest_inspection_date ?? "",
    fotos: 0,
    observacao: "",
    fotoUrl: "",
  };
}

export function mapInspectionFromApi(api: InspectionApi): Vistoria {
  return {
    id: api.uuid,
    codigoCta: api.cta_code ?? "",
    especie: api.species_popular_name ?? "",
    nomePopular: api.species_popular_name ?? "",
    projeto: api.project_name,
    data: api.inspection_date,
    tipo: api.inspection_type,
    tecnico: api.technician_name ?? "",
    estadoFitossanitario: "",
    classeRisco: "",
    recomendacao: "",
    fotos: 0,
    status: api.status,
    dap: 0,
    altura: 0,
    diametroCopa: 0,
    parecer: api.notes ?? "",
    sintomas: [],
    interferencias: [],
    municipio: "",
    uf: "",
    bairro: "",
    logradouro: "",
    coordenada: "",
    cicloId: null,
  };
}

const LINKED_TABLE_TO_BINDING_TYPE: Record<string, DocumentItem["primaryBindingType"]> = {
  tree_individuals: "cta",
  projects: "project",
  environmental_units: "area",
  party: "none",
};

export function mapDocumentFromApi(api: DocumentApi): DocumentItem {
  return {
    id: api.uuid,
    type: api.document_type,
    title: api.title,
    number: api.document_number ?? "",
    projectId: api.project_id != null ? String(api.project_id) : null,
    projectName: api.project_name,
    primaryBinding: api.linked_entity_name ?? "",
    primaryBindingType: LINKED_TABLE_TO_BINDING_TYPE[api.linked_entity_table ?? ""] ?? "none",
    additionalBindings: [],
    municipality: "",
    uf: "",
    responsible: "",
    issuingBody: api.issuing_body ?? "",
    issueDate: api.issue_date ?? "",
    expiryDate: api.expiration_date,
    status: api.status,
    description: api.description ?? "",
    version: "",
    replacedById: null,
    fileType: api.mime_type ?? "",
    hasFile: (api.file_count ?? 0) > 0,
    tags: [],
    gisMetadata: null,
    ctaCode: api.linked_entity_table === "tree_individuals" ? api.linked_entity_name : null,
    areaId: null,
    areaName: api.linked_entity_table === "environmental_units" ? api.linked_entity_name : null,
    inspectionId: null,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function mapPartyFromApi(api: PartyApi): Party {
  return {
    id: api.uuid,
    name: api.name,
    type: api.party_type,
    documentId: api.document_number ?? "",
    email: api.email ?? "",
    phone: api.phone ?? "",
    address: api.address ?? "",
    municipality: api.municipality_name ?? "",
    uf: api.uf ?? "",
    institution: api.legal_name ?? "",
    primaryRole: api.role_types ?? "",
    status: api.status,
    activeLinks: api.project_link_count + api.spatial_unit_link_count,
    hasDocuments: api.document_count > 0,
    registrationDate: api.created_at,
    lastUpdate: api.updated_at,
  };
}

const SEVERITY_LABELS: Record<string, string> = {
  CRITICA: "crítica",
  ALTA: "alta",
  MEDIA: "média",
  BAIXA: "baixa",
  INFORMATIVA: "informativa",
};

export function mapValidationIssueFromApi(api: ValidationIssueApi): Inconsistency {
  return {
    id: `${api.rule_code}-${api.entity_id}`,
    severidade: SEVERITY_LABELS[api.severity] ?? api.severity.toLowerCase(),
    regra: api.rule_code,
    codigoCta: "",
    projeto: api.project_id != null ? String(api.project_id) : "",
    descricao: api.message,
    responsavel: "",
    detectadaEm: api.detected_at,
    status: "pendente",
    acaoRecomendada: "",
  };
}

function qualityStatusFromScore(score: number): ProjectQualityRank["status"] {
  if (score >= 90) return "excelente";
  if (score >= 80) return "bom";
  if (score >= 72) return "regular";
  return "atenção";
}

export function mapQualitySummaryFromApi(api: QualitySummaryApi): ProjectQualityRank {
  return {
    projeto: api.project_name,
    score: api.estimated_quality_score,
    arvores: 0,
    inconsistencias: api.open_issue_count,
    status: qualityStatusFromScore(api.estimated_quality_score),
  };
}

const CONFIDENCE_LABELS: Record<string, string> = {
  ALTA: "alta",
  MEDIA: "media",
  BAIXA: "baixa",
};

const VALIDATION_STATUS_LABELS: Record<string, string> = {
  validado: "validado",
  divergente: "divergente",
  sem_3d: "sem_3d",
  // sem medição de campo é tratado como "sem_3d" na UI — não há uma 4ª categoria no filtro hoje.
  sem_campo: "sem_3d",
};

export function mapTreeMetrics3DFromApi(api: TreeMetrics3DApi): TreeMetric3DRow {
  return {
    codigoCta: api.cta_code ?? "",
    especie: api.species_popular_name ?? "",
    projeto: api.project_id != null ? String(api.project_id) : "",
    bairro: "",
    alturaCampo: api.field_height_m,
    altura3D: api.height_3d_m,
    diametroCopaCampo: api.field_crown_diameter_m,
    diametroCopa3D: api.crown_diameter_3d_m,
    volumeCopa: api.crown_volume_3d_m3,
    inclinacao: api.inclination_3d_deg,
    confianca: api.confidence_level ? CONFIDENCE_LABELS[api.confidence_level] ?? "-" : "-",
    risco: "",
    status: VALIDATION_STATUS_LABELS[api.validation_status] ?? api.validation_status,
    fonte3D: api.point_cloud_name ?? "",
  };
}

function mapTerritorialInspectionSummaryFields(
  api: TerritorialInspectionSummaryApi
): TerritorialInspectionSummary {
  return {
    inspectionId: api.inspection_id,
    uuid: api.uuid,
    inspectionCode: api.inspection_code,
    tenantId: api.tenant_id,
    neighborhood: api.neighborhood ?? "",
    blockCode: api.block_code ?? "",
    previousInspectionCode: api.previous_inspection_code,
    inspectionDate: api.inspection_date,
    sourceType: api.source_type,
    sourceReference: api.source_reference,
    imageUri: api.image_uri,
    imageFilename: api.image_filename,
    responsiblePartyName: api.responsible_party_name,
    processingStatus: api.processing_status,
    dataClassification: api.data_classification,
    intendedUse: api.intended_use,
    fieldValidationRequired: api.field_validation_required,
    fieldValidationStatus: api.field_validation_status,
    status: api.status,
    confidenceScore: api.confidence_score,
    estimatedTreeCount: api.estimated_tree_count,
    canopyCoveragePercent: api.canopy_coverage_percent,
    plantingPotentialCount: api.planting_potential_count,
    treeDeficitCount: api.tree_deficit_count,
    riskTreeCount: api.risk_tree_count,
    fieldValidationNotesCount: api.field_validation_notes_count,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function mapTerritorialInspectionSummaryFromApi(
  api: TerritorialInspectionSummaryApi
): TerritorialInspectionSummary {
  return mapTerritorialInspectionSummaryFields(api);
}

export function mapTerritorialInspectionDetailFromApi(
  api: TerritorialInspectionDetailApi
): TerritorialInspectionDetail {
  const roads: TerritorialRoad[] = api.roads.map((r) => ({
    roadName: r.road_name,
    roadType: r.road_type,
    pavementType: r.pavement_type,
    pavementCondition: r.pavement_condition,
    sidewalkCondition: r.sidewalk_condition,
    drainageCondition: r.drainage_condition,
    widthM: r.width_m,
    plantingPotentialCount: r.planting_potential_count,
    treeConflictCount: r.tree_conflict_count,
    notes: r.notes,
  }));

  const landUse: TerritorialLandUse[] = api.land_use.map((l) => ({
    landUseType: l.land_use_type,
    areaPercent: l.area_percent,
    areaM2: l.area_m2,
    intensityLevel: l.intensity_level,
    notes: l.notes,
  }));

  const coverage: TerritorialCoverage[] = api.coverage.map((c) => ({
    coverageType: c.coverage_type,
    areaPercent: c.area_percent,
    areaM2: c.area_m2,
    permeabilityClass: c.permeability_class,
    heatIslandWeight: c.heat_island_weight,
    confidenceScore: c.confidence_score,
    notes: c.notes,
  }));

  const treeInventorySummary: TerritorialTreeInventorySummary | null = api.tree_inventory_summary
    ? {
        estimatedTreeCount: api.tree_inventory_summary.estimated_tree_count,
        adultTreeCount: api.tree_inventory_summary.adult_tree_count,
        youngTreeCount: api.tree_inventory_summary.young_tree_count,
        palmCount: api.tree_inventory_summary.palm_count,
        shrubCount: api.tree_inventory_summary.shrub_count,
        canopyCoveragePercent: api.tree_inventory_summary.canopy_coverage_percent,
        estimatedCanopyAreaM2: api.tree_inventory_summary.estimated_canopy_area_m2,
        plantingPotentialCount: api.tree_inventory_summary.planting_potential_count,
        treeDeficitCount: api.tree_inventory_summary.tree_deficit_count,
        riskTreeCount: api.tree_inventory_summary.risk_tree_count,
        speciesNotes: api.tree_inventory_summary.species_notes,
        notes: api.tree_inventory_summary.notes,
      }
    : null;

  const indicators: TerritorialIndicator[] = api.indicators.map((i) => ({
    indicatorCode: i.indicator_code,
    indicatorName: i.indicator_name,
    category: i.category,
    valueNumeric: i.value_numeric,
    valueText: i.value_text,
    unit: i.unit,
    riskLevel: i.risk_level,
    confidenceScore: i.confidence_score,
    notes: i.notes,
  }));

  const recommendations: TerritorialRecommendation[] = api.recommendations.map((r) => ({
    recommendationType: r.recommendation_type,
    priority: r.priority,
    description: r.description,
    targetLocation: r.target_location,
    estimatedQuantity: r.estimated_quantity,
    expectedImpact: r.expected_impact,
    status: r.status,
  }));

  return {
    ...mapTerritorialInspectionSummaryFields(api),
    reportTitle: api.report_title,
    reportText: api.report_text,
    roads,
    landUse,
    coverage,
    treeInventorySummary,
    indicators,
    recommendations,
  };
}

export function mapDetectedObjectFromApi(api: DetectedObjectApi): TerritorialDetectedObject {
  return {
    id: api.id,
    uuid: api.uuid,
    objectCode: api.object_code,
    objectType: api.object_type,
    objectLabel: api.object_label,
    confidenceScore: api.confidence_score,
    detectionMethod: api.detection_method,
    heightEstimatedM: api.height_estimated_m,
    crownDiameterEstimatedM: api.crown_diameter_estimated_m,
    canopyAreaM2: api.canopy_area_m2,
    conditionStatus: api.condition_status,
    riskLevel: api.risk_level,
    notes: api.notes,
  };
}

export function mapFieldValidationNoteFromApi(api: FieldValidationNoteApi): TerritorialFieldValidationNote {
  return {
    id: api.id,
    uuid: api.uuid,
    noteType: api.note_type,
    description: api.description,
    priority: api.priority,
    status: api.status,
    responsiblePartyName: api.responsible_party_name,
    createdAt: api.created_at,
  };
}

export function mapTerritorialIndicatorsSummaryFromApi(
  api: TerritorialIndicatorsSummaryApi
): TerritorialIndicatorsSummary {
  return {
    avgTreeCoverageByNeighborhood: api.avg_tree_coverage_by_neighborhood.map((i) => ({
      neighborhood: i.neighborhood,
      totalInspections: i.total_inspections,
      avgCanopyCoveragePercent: i.avg_canopy_coverage_percent,
      avgEstimatedTreeCount: i.avg_estimated_tree_count,
    })),
    heatIslandRanking: api.heat_island_ranking.map((i) => ({
      neighborhood: i.neighborhood,
      blockCode: i.block_code,
      heatIslandRiskScore: i.heat_island_risk_score,
      riskLevel: i.risk_level,
    })),
    estimatedTreesByBlock: api.estimated_trees_by_block.map((i) => ({
      neighborhood: i.neighborhood,
      blockCode: i.block_code,
      estimatedTreeCount: i.estimated_tree_count,
      riskTreeCount: i.risk_tree_count,
      plantingPotentialCount: i.planting_potential_count,
    })),
    plantingPotentialByRoad: api.planting_potential_by_road.map((i) => ({
      roadName: i.road_name,
      roadSegments: i.road_segments,
      totalPlantingPotential: i.total_planting_potential,
    })),
    avgImpermeabilization: api.avg_impermeabilization.map((i) => ({
      neighborhood: i.neighborhood,
      avgImpermeabilizationPercent: i.avg_impermeabilization_percent,
    })),
    treeDeficitRanking: api.tree_deficit_ranking.map((i) => ({
      neighborhood: i.neighborhood,
      blockCode: i.block_code,
      treeDeficitCount: i.tree_deficit_count,
      plantingPotentialCount: i.planting_potential_count,
      canopyCoveragePercent: i.canopy_coverage_percent,
    })),
  };
}

// --- Editor Territorial WebGIS -------------------------------------------

export function mapRascunhoFeicaoFromApi(api: RascunhoFeicaoApi): RascunhoFeicao {
  return {
    id: api.id,
    projetoId: api.projeto_id,
    quadraId: api.quadra_id,
    projetoRefId: api.projeto_ref_id,
    quadraRefId: api.quadra_ref_id,
    areaPilotoRefId: api.area_piloto_ref_id,
    camadaDestino: api.camada_destino,
    tipoObjeto: api.tipo_objeto,
    tipoGeometria: api.tipo_geometria,
    geom: api.geom,
    propriedades: api.propriedades,
    status: api.status as RascunhoStatus,
    fonte: api.fonte,
    validacao: api.validacao,
    areaM2: api.area_m2,
    comprimentoM: api.comprimento_m,
    perimetroM: api.perimetro_m,
    publicadoSchema: api.publicado_schema,
    publicadoTabela: api.publicado_tabela,
    publicadoId: api.publicado_id,
    criadoPor: api.criado_por,
    revisadoPor: api.revisado_por,
    aprovadoPor: api.aprovado_por,
    publicadoPor: api.publicado_por,
    criadoEm: api.criado_em,
    atualizadoEm: api.atualizado_em,
    revisadoEm: api.revisado_em,
    aprovadoEm: api.aprovado_em,
    publicadoEm: api.publicado_em,
  };
}

export function mapCamadaGeoFromApi(api: CamadaGeoApi): CamadaGeo {
  return {
    id: api.id,
    nome: api.nome,
    schemaName: api.schema_name,
    tableName: api.table_name,
    camadaDestino: api.camada_destino,
    tipoObjeto: api.tipo_objeto,
    tipoGeometria: api.tipo_geometria,
    descricao: api.descricao,
    status: api.status,
  };
}

export function mapCamadaFeatureFromApi(api: CamadaFeatureApi): CamadaFeature {
  return {
    id: api.id,
    rascunhoId: api.rascunho_id,
    quadraId: api.quadra_id,
    geom: api.geom,
    propriedades: api.propriedades,
    fonte: api.fonte,
    validacao: api.validacao,
    publicadoEm: api.publicado_em,
  };
}

export function mapRascunhoPublishResultFromApi(api: RascunhoPublishResultApi): RascunhoPublishResult {
  return {
    rascunhoId: api.rascunho_id,
    camadaDestino: api.camada_destino,
    publicadoId: api.publicado_id,
    objetoTerritorialId: api.objeto_territorial_id,
    status: api.status,
  };
}

// --- Arboricultural Inspections (Inventário Arbóreo / Vistoria Técnica) ---

function mapMeasurementFromApi(api: ArborMeasurementApi): TreeMeasurement {
  return {
    id: api.id,
    dapCm: api.dap_cm,
    capCm: api.cap_cm,
    heightM: api.height_m,
    crownDiameterM: api.crown_diameter_m,
    inclinationDeg: api.inclination_deg,
    measurementSource: api.measurement_source,
    createdAt: api.created_at,
  };
}

function mapStemFromApi(api: ArborStemMeasurementApi): StemMeasurement {
  return {
    id: api.id,
    uuid: api.uuid,
    sequence: api.sequence,
    capCm: api.cap_cm,
    dapCm: api.dap_cm,
    measurementMethod: api.measurement_method,
    heightM: api.height_m,
    notes: api.notes,
    createdAt: api.created_at,
  };
}

function mapHealthAssessmentFromApi(api: ArborHealthAssessmentApi): HealthAssessment {
  return {
    id: api.id,
    generalStatus: api.general_status,
    phytosanitaryClass: api.phytosanitary_class ?? null,
    rootCondition: api.root_condition ?? null,
    rootNotes: api.root_notes ?? null,
    pests: api.pests,
    diseases: api.diseases,
    fungi: api.fungi,
    termites: api.termites,
    cavities: api.cavities,
    rot: api.rot,
    dryBranches: api.dry_branches,
    exposedRoots: api.exposed_roots,
    abnormalInclination: api.abnormal_inclination,
    inclinationAngleDeg: api.inclination_angle_deg,
    inclinationDirection: api.inclination_direction,
    exposedRootsDetail: api.exposed_roots_detail,
    underminedRoots: api.undermined_roots,
    damagedRoots: api.damaged_roots,
    constructionDamage: api.construction_damage,
    soilCompaction: api.soil_compaction,
    rootPlateMovement: api.root_plate_movement,
    notes: api.notes,
    createdAt: api.created_at,
  };
}

function mapHealthFindingFromApi(api: ArborHealthFindingApi): HealthFinding {
  return {
    id: api.id,
    uuid: api.uuid,
    findingType: api.finding_type,
    location: api.location,
    severity: api.severity,
    notes: api.notes,
    createdAt: api.created_at,
  };
}

function mapMechanicalFindingFromApi(api: ArborMechanicalFindingApi): MechanicalFinding {
  return {
    id: api.id,
    uuid: api.uuid,
    findingType: api.finding_type,
    location: api.location,
    severity: api.severity,
    notes: api.notes,
    createdAt: api.created_at,
  };
}

function mapRiskAssessmentFromApi(api: ArborRiskAssessmentApi): RiskAssessment {
  return {
    id: api.id,
    riskClass: api.risk_class,
    riskScore: api.risk_score,
    priority: api.priority,
    pedestrians: api.pedestrians,
    vehicles: api.vehicles,
    buildings: api.buildings,
    powerGrid: api.power_grid,
    riskCriteria: api.risk_criteria,
    targetClass: api.target_class ?? null,
    priorityClass: api.priority_class ?? null,
    priorityDueAt: api.priority_due_at ?? null,
    priorityDeadlineLabel: api.priority_deadline_label ?? null,
    prioritySuggested: api.priority_suggested ?? null,
    prioritySuggestionReasons: api.priority_suggestion_reasons ?? [],
    notes: api.notes,
    createdAt: api.created_at,
  };
}

function mapRiskTargetFromApi(api: ArborRiskTargetApi): RiskTarget {
  return {
    id: api.id,
    uuid: api.uuid,
    targetType: api.target_type,
    occupancy: api.occupancy,
    notes: api.notes,
    createdAt: api.created_at,
  };
}

function mapInterferenceFromApi(api: ArborInterferenceApi): Interference {
  return {
    id: api.id,
    interferenceType: api.interference_type,
    intensity: api.intensity,
    description: api.description,
    createdAt: api.created_at,
  };
}

function mapRecommendationFromApi(api: ArborRecommendationApi): Recommendation {
  return {
    id: api.id,
    recommendationType: api.recommendation_type,
    priority: api.priority,
    justification: api.justification,
    status: api.status,
    createdAt: api.created_at,
  };
}

function mapEvidenceFromApi(api: ArborEvidenceApi): Evidence {
  return {
    id: api.id,
    uuid: api.uuid,
    evidenceType: api.evidence_type,
    filePath: api.file_path,
    thumbnailPath: api.thumbnail_path,
    originalFilename: api.original_filename,
    mimeType: api.mime_type,
    fileSizeBytes: api.file_size_bytes,
    description: api.description,
    latitude: api.latitude,
    longitude: api.longitude,
    capturedAt: api.captured_at,
    uploadedAt: api.uploaded_at,
    createdAt: api.created_at,
    invalidatedAt: api.invalidated_at ?? null,
    invalidatedBy: api.invalidated_by ?? null,
    invalidationReason: api.invalidation_reason ?? null,
  };
}

function mapCompensationFromApi(api: ArborCompensationApi): Compensation {
  return {
    id: api.id,
    uuid: api.uuid,
    compensationIndicated: api.compensation_indicated,
    proposedSeedlingQuantity: api.proposed_seedling_quantity,
    proposedSpecies: api.proposed_species,
    notes: api.notes,
    createdAt: api.created_at,
  };
}

export function mapArborInspectionSummaryFromApi(
  api: ArborInspectionSummaryApi
): ArborInspectionSummary {
  return {
    id: api.id,
    uuid: api.uuid,
    treeId: api.tree_id,
    inspectionType: api.inspection_type,
    inspectionDate: api.inspection_date,
    workflowStatus: api.workflow_status as ArborInspectionSummary["workflowStatus"],
    inspectorName: api.inspector_name,
    notes: api.notes,
    createdAt: api.created_at,
    dapCm: api.dap_cm,
    heightM: api.height_m,
    healthStatus: api.health_status,
    riskClass: api.risk_class,
    riskPriority: api.risk_priority,
    evidenceCount: api.evidence_count,
    recommendationCount: api.recommendation_count,
  };
}

export function mapArborInspectionDetailFromApi(
  api: ArborInspectionDetailApi
): ArborInspectionDetail {
  return {
    id: api.id,
    uuid: api.uuid,
    tenantId: api.tenant_id,
    projectId: api.project_id,
    treeId: api.tree_id,
    inspectionType: api.inspection_type,
    inspectionDate: api.inspection_date,
    workflowStatus: api.workflow_status as ArborInspectionDetail["workflowStatus"],
    status: api.status,
    inspectorName: api.inspector_name,
    professionalCouncil: api.professional_council,
    professionalRegistration: api.professional_registration,
    locationConfirmed: api.location_confirmed,
    botanicalIdentificationStatus: api.botanical_identification_status,
    botanicalNotes: api.botanical_notes,
    diagnosis: api.diagnosis,
    technicalOpinion: api.technical_opinion,
    requiresMonitoring: api.requires_monitoring,
    nextInspectionSuggestedAt: api.next_inspection_suggested_at,
    notes: api.notes,
    version: api.version,
    submittedAt: api.submitted_at,
    submittedBy: api.submitted_by,
    submittedByName: api.submitted_by_name ?? null,
    validatedAt: api.validated_at,
    validatedBy: api.validated_by,
    validatedByName: api.validated_by_name ?? null,
    returnedAt: api.returned_at,
    returnedBy: api.returned_by,
    returnedByName: api.returned_by_name ?? null,
    returnReason: api.return_reason,
    cancelledAt: api.cancelled_at,
    cancelledBy: api.cancelled_by,
    cancelledByName: api.cancelled_by_name ?? null,
    cancelReason: api.cancel_reason ?? null,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    measurement: api.measurement ? mapMeasurementFromApi(api.measurement) : null,
    stems: api.stems.map(mapStemFromApi),
    healthAssessment: api.health_assessment ? mapHealthAssessmentFromApi(api.health_assessment) : null,
    healthFindings: api.health_findings.map(mapHealthFindingFromApi),
    mechanicalFindings: api.mechanical_findings.map(mapMechanicalFindingFromApi),
    riskAssessment: api.risk_assessment ? mapRiskAssessmentFromApi(api.risk_assessment) : null,
    riskTargets: api.risk_targets.map(mapRiskTargetFromApi),
    interferences: api.interferences.map(mapInterferenceFromApi),
    recommendations: api.recommendations.map(mapRecommendationFromApi),
    evidence: api.evidence.map(mapEvidenceFromApi),
    compensation: api.compensation ? mapCompensationFromApi(api.compensation) : null,
  };
}

// --- Território > Área piloto / Quadras (camada operacional real) --------

export function mapAreaPilotoFromApi(api: AreaPilotoApi): AreaPiloto {
  return {
    id: api.id,
    uuid: api.uuid,
    tenantId: api.tenant_id,
    projectId: api.project_id,
    municipalityId: api.municipality_id,
    municipalityName: api.municipality_name,
    uf: api.uf,
    name: api.name,
    code: api.code,
    neighborhood: api.neighborhood,
    expectedBlockCount: api.expected_block_count,
    totalQuadras: api.total_quadras,
    totalTrees: api.total_trees,
    totalPostes: api.total_postes,
    totalRedesAereas: api.total_redes_aereas,
    totalConflitosAltaCritica: api.total_conflitos_alta_critica,
    areaHa: api.area_ha,
    geom: api.geom,
    status: api.status,
    updatedAt: api.updated_at,
  };
}

export function mapQuadraFromApi(api: QuadraApi): Quadra {
  return {
    id: api.id,
    uuid: api.uuid,
    tenantId: api.tenant_id,
    areaPilotoId: api.area_piloto_id,
    areaPilotoName: api.area_piloto_name,
    spatialUnitId: api.spatial_unit_id,
    code: api.code,
    name: api.name,
    neighborhood: api.neighborhood,
    areaM2: api.area_m2,
    perimeterM: api.perimeter_m,
    totalTrees: api.total_trees,
    totalIndividuosArboreos: api.total_individuos_arboreos,
    totalPostes: api.total_postes,
    totalRedesAereas: api.total_redes_aereas,
    totalInterferencias: api.total_interferencias,
    totalInterferenciasCriticas: api.total_interferencias_criticas,
    areaPermeavelM2: api.area_permeavel_m2,
    areaImpermeavelM2: api.area_impermeavel_m2,
    geom: api.geom,
    status: api.status,
    updatedAt: api.updated_at,
  };
}

export function mapInspectionQuadraLinkFromApi(api: InspectionQuadraLinkApi): InspectionQuadraLink {
  return {
    inspectionId: api.inspection_id,
    inspectionUuid: api.inspection_uuid,
    inspectionCode: api.inspection_code,
    tenantId: api.tenant_id,
    projectId: api.project_id,
    inspectionAreaPilotoId: api.inspection_area_piloto_id,
    quadraId: api.quadra_id,
    quadraUuid: api.quadra_uuid,
    quadraAreaPilotoId: api.quadra_area_piloto_id,
    quadraCode: api.quadra_code,
    quadraName: api.quadra_name,
    inspectionBlockCode: api.inspection_block_code,
    inspectionNeighborhood: api.inspection_neighborhood,
    quadraNeighborhood: api.quadra_neighborhood,
    inspectionStatus: api.inspection_status,
    processingStatus: api.processing_status,
    fieldValidationStatus: api.field_validation_status,
    aiModel: api.ai_model,
    analysisVersion: api.analysis_version,
    inspectionDate: api.inspection_date,
    imageFilename: api.image_filename,
    confidenceScore: api.confidence_score,
    linkMethod: api.link_method,
    linkConfidence: api.link_confidence,
    totalCodeMatches: api.total_code_matches,
    treeCanopyPercent: api.tree_canopy_percent,
    imperviousAreaPercent: api.impervious_area_percent,
    heatIslandRisk: api.heat_island_risk,
    treeDeficitLevel: api.tree_deficit_level,
    estimatedTreeCount: api.estimated_tree_count,
    plantingPotentialCount: api.planting_potential_count,
    inventoryCanopyCoveragePercent: api.inventory_canopy_coverage_percent,
    quadraAreaM2: api.quadra_area_m2,
    quadraPerimeterM: api.quadra_perimeter_m,
    geom: api.geom,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function mapCampaignSummaryFromApi(api: CampaignSummaryApi): CampaignSummary {
  return {
    id: api.id,
    uuid: api.uuid,
    tenantId: api.tenant_id,
    projectId: api.project_id,
    projectName: api.project_name,
    areaPilotoId: api.area_piloto_id,
    areaPilotoName: api.area_piloto_name,
    code: api.code,
    name: api.name,
    campaignType: api.campaign_type,
    status: api.status,
    description: api.description,
    startDate: api.start_date,
    endDate: api.end_date,
    targetTreeCount: api.target_tree_count,
    notes: api.notes,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    memberCount: api.member_count,
    quadraCount: api.quadra_count,
    quadrasConcluidas: api.quadras_concluidas,
    registeredTreeCount: api.registered_tree_count,
    progressPercent: api.progress_percent,
  };
}

export function mapCampaignMemberFromApi(api: CampaignMemberApi): CampaignMember {
  return {
    id: api.id,
    campaignId: api.campaign_id,
    userId: api.user_id,
    partyId: api.party_id,
    memberRole: api.member_role,
    memberName: api.member_name,
    professionalCouncil: api.professional_council,
    professionalRegistration: api.professional_registration,
    isActive: api.is_active,
    joinedAt: api.joined_at,
  };
}

export function mapCampaignQuadraProgressFromApi(api: CampaignQuadraProgressApi): CampaignQuadraProgress {
  return {
    id: api.id,
    campaignId: api.campaign_id,
    campaignCode: api.campaign_code,
    campaignName: api.campaign_name,
    quadraId: api.quadra_id,
    quadraCode: api.quadra_code,
    quadraName: api.quadra_name,
    status: api.status,
    targetTreeCount: api.target_tree_count,
    registeredTreeCount: api.registered_tree_count,
    startedAt: api.started_at,
    completedAt: api.completed_at,
    notes: api.notes,
    progressPercent: api.progress_percent,
  };
}

export function mapTreeCreateResponseFromApi(api: TreeCreateResponseApi): TreeCreateResult {
  return {
    id: api.id,
    uuid: api.uuid,
    ctaCode: api.cta_code,
    status: api.status,
    duplicateOf: api.duplicate_of,
    isNew: api.is_new,
  };
}


export function mapDashboardOverviewFromApi(api: DashboardOverviewApi): DashboardOverview {
  return {
    totalTrees: api.total_trees,
    treesWithoutValidatedInspection: api.trees_without_validated_inspection,
    totalProjects: api.total_projects,
    activeProjects: api.active_projects,
    totalSpeciesInUse: api.total_species_in_use,
    totalInspections: api.total_inspections,
    inspectionsByStatus: api.inspections_by_status,
    pendingReview: api.pending_review,
    activeCampaigns: api.active_campaigns,
    healthDistribution: api.health_distribution,
    riskDistribution: api.risk_distribution,
    topSpecies: api.top_species,
    registrationOrigin: api.registration_origin,
    monthlyActivity: api.monthly_activity.map((r) => ({
      month: r.month,
      treesRegistered: r.trees_registered,
      inspectionsDone: r.inspections_done,
    })),
    recentInspections: api.recent_inspections.map((r) => ({
      id: r.id,
      treeId: r.tree_id,
      treeCode: r.tree_code,
      speciesName: r.species_name,
      inspectionType: r.inspection_type,
      workflowStatus: r.workflow_status,
      inspectionDate: r.inspection_date,
      inspectorName: r.inspector_name,
    })),
    attentionTrees: api.attention_trees.map((r) => ({
      treeId: r.tree_id,
      treeCode: r.tree_code,
      speciesName: r.species_name,
      riskClass: r.risk_class,
      primaryRecommendation: r.primary_recommendation,
      lastInspectionDate: r.last_inspection_date,
    })),
    projects: api.projects.map((r) => ({ id: r.id, name: r.name, status: r.status, treeCount: r.tree_count })),
  };
}


export function mapBairroFromApi(api: BairroApi): Bairro {
  return {
    id: api.id,
    name: api.name,
    region: api.region,
    population: api.population,
    treeCount: api.tree_count,
    assessedCount: api.assessed_count,
    highRiskCount: api.high_risk_count,
    poorHealthCount: api.poor_health_count,
    analyzedBlocks: api.analyzed_blocks,
    avgCanopyPercent: api.avg_canopy_percent,
    avgImperviousPercent: api.avg_impervious_percent,
    highHeatBlocks: api.high_heat_blocks,
    highDeficitBlocks: api.high_deficit_blocks,
    estimatedTreeCount: api.estimated_tree_count,
    plantingPotentialCount: api.planting_potential_count,
  };
}

export function mapAreaTreeInventoryFromApi(api: AreaTreeInventoryApi): AreaTreeInventory {
  return {
    areaPilotoId: api.area_piloto_id,
    maxDistanceM: api.max_distance_m,
    quadrasTotal: api.quadras_total,
    quadrasWithEstimate: api.quadras_with_estimate,
    quadrasWithRegistered: api.quadras_with_registered,
    estimatedTotal: api.estimated_total,
    registeredTotal: api.registered_total,
    validatedTotal: api.validated_total,
    highRiskTotal: api.high_risk_total,
    quadras: api.quadras.map((q) => ({
      quadraId: q.quadra_id,
      quadraCode: q.quadra_code,
      quadraName: q.quadra_name,
      inspectionCode: q.inspection_code,
      inspectionDate: q.inspection_date,
      aiModel: q.ai_model,
      estimatedTreeCount: q.estimated_tree_count,
      plantingPotentialCount: q.planting_potential_count,
      riskTreeCount: q.risk_tree_count,
      registeredCount: q.registered_count,
      validatedCount: q.validated_count,
      highRiskCount: q.high_risk_count,
      trees: q.trees.map((t) => ({
        id: t.id,
        ctaCode: t.cta_code,
        status: t.status,
        street: t.street,
        createdAt: t.created_at,
        speciesPopularName: t.species_popular_name,
        speciesScientificName: t.species_scientific_name,
        latitude: t.latitude,
        longitude: t.longitude,
        distanceM: t.distance_m,
        insideQuadra: t.inside_quadra,
        latestValidatedInspectionDate: t.latest_validated_inspection_date,
        latestValidatedRiskClass: t.latest_validated_risk_class,
      })),
    })),
  };
}

function camelizeDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(camelizeDeep);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase()),
        camelizeDeep(v),
      ]),
    );
  }
  return value;
}

/** Os códigos (F1, ACIMA_60, RUA...) são valores, não chaves: só as chaves mudam de caixa. */
export function mapTreeStatisticsFromApi(api: TreeStatisticsApi): TreeStatistics {
  return camelizeDeep(api) as TreeStatistics;
}
